/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/components/auth-context"
import type { Client, ClientWithStats } from "@/types/client"

interface ClientsContextType {
    clients: ClientWithStats[] // Active clients (non-deleted)
    trashClients: ClientWithStats[] // Soft-deleted clients
    isLoading: boolean
    fetchClients: () => Promise<void>
    createClient: (data: Partial<Client>) => Promise<Client | null>
    updateClient: (clientId: string, updates: Partial<Client>) => Promise<Client | null>
    deleteClient: (clientId: string, permanent?: boolean) => Promise<boolean>
    restoreClient: (clientId: string) => Promise<boolean>
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined)

export function ClientsProvider({ children }: { children: React.ReactNode }) {
    const { organization } = useAuth()
    const [allClients, setAllClients] = useState<ClientWithStats[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const fetchClients = useCallback(async () => {
        if (!organization) {
            setAllClients([])
            setIsLoading(false)
            return
        }

        setIsLoading(true)

        try {
            const { data, error } = await supabase
                .from('clients')
                .select(`
                    *,
                    projects(id, name, color, status, hourly_rate, time_entries(total_hours))
                `)
                .eq('organization_id', organization.id)
                .order('name', { ascending: true })

            if (error) throw error

            if (data) {
                type ProjectData = { status: string; hourly_rate: number | null; time_entries?: { total_hours: number | null }[] | null }
                type DbClient = Client & { projects?: ProjectData[] }

                const clientsWithStats = (data as unknown as DbClient[]).map((c: DbClient) => {
                    const projects = c.projects || []
                    const totalProjects = projects.length
                    const activeProjects = projects.filter((p: ProjectData) => p.status === 'active').length

                    // Calcular horas y revenue totales
                    const totalHours = projects.reduce((acc: number, p: ProjectData) => {
                        const projectHours = p.time_entries?.reduce((sum: number, te: { total_hours: number | null }) =>
                            sum + (te.total_hours || 0), 0) || 0
                        return acc + projectHours
                    }, 0)

                    const totalRevenue = projects.reduce((acc: number, p: ProjectData) => {
                        const projectHours = p.time_entries?.reduce((sum: number, te: { total_hours: number | null }) =>
                            sum + (te.total_hours || 0), 0) || 0
                        return acc + (projectHours * (p.hourly_rate || 0))
                    }, 0)

                    const { projects: _discarded, ...clientData } = c as DbClient

                    return {
                        ...clientData,
                        stats: {
                            totalProjects,
                            activeProjects,
                            totalHours,
                            totalRevenue
                        }
                    }
                })
                setAllClients(clientsWithStats)
            }
        } catch (error) {
            console.error("Error fetching clients:", error)
        } finally {
            setIsLoading(false)
        }
    }, [organization])

    useEffect(() => {
        fetchClients()
    }, [fetchClients])

    // Derived lists
    const clients = useMemo(() => allClients.filter(c => !c.deleted_at), [allClients])
    const trashClients = useMemo(() => allClients.filter(c => c.deleted_at), [allClients])

    const createClient = async (data: Partial<Client>) => {
        if (!organization) return null

        const { data: newClient, error } = await supabase
            .from('clients')
            .insert({
                organization_id: organization.id,
                ...data
            })
            .select()
            .single()

        if (error) {
            console.error("Error creating client:", error)
            return null
        }

        await fetchClients()
        return newClient
    }

    const updateClient = async (clientId: string, updates: Partial<Client>) => {
        const { data, error } = await supabase
            .from('clients')
            .update(updates)
            .eq('id', clientId)
            .select()
            .single()

        if (error) {
            console.error("Error updating client:", error)
            return null
        }

        await fetchClients()
        return data
    }

    const deleteClient = async (clientId: string, permanent: boolean = false) => {
        if (permanent) {
            const { error } = await supabase
                .from('clients')
                .delete()
                .eq('id', clientId)

            if (error) {
                console.error("Error permanently deleting client:", error)
                return false
            }
        } else {
            // First check if client has any projects (active or archived)
            const { count, error: countError } = await supabase
                .from('projects')
                .select('*', { count: 'exact', head: true })
                .eq('client_id', clientId)

            if (countError) {
                console.error("Error checking client projects:", countError)
                return false
            }

            console.log(`Archiving client with ${count || 0} projects`)

            // Perform Soft Delete
            const { error } = await supabase
                .from('clients')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', clientId)

            if (error) {
                console.error("Error archiving client:", error)
                return false
            }
        }

        await fetchClients()
        return true
    }

    const restoreClient = async (clientId: string) => {
        const { error } = await supabase
            .from('clients')
            .update({ deleted_at: null })
            .eq('id', clientId)

        if (error) {
            console.error("Error restoring client:", error)
            return false
        }

        await fetchClients()
        return true
    }

    return (
        <ClientsContext.Provider value={{
            clients,
            trashClients,
            isLoading,
            fetchClients,
            createClient,
            updateClient,
            deleteClient,
            restoreClient
        }}>
            {children}
        </ClientsContext.Provider>
    )
}

export function useClientsContext() {
    const context = useContext(ClientsContext)
    if (context === undefined) {
        throw new Error("useClientsContext must be used within a ClientsProvider")
    }
    return context
}
